import React from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const AdminDetailsModal = ({ item, schema, onClose }) => {
  const API_BASE =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.VITE_API_BASE_URL ||
    "https://nouzhaback-theta.vercel.app";

  const resolveImageUrl = (value) => {
    if (!value || typeof value !== "string") return "";
    return value.startsWith("http") ? value : `${API_BASE}${value}`;
  };

  const getFieldDisplayName = (fieldName) => {
    const translations = {
      _id: "ID",
      createdAt: "Créé le",
      updatedAt: "Modifié le",
      nom: "Nom",
      prenom: "Prénom",
      email: "Email",
      username: "Nom d'utilisateur",
      role: "Rôle",
      profileImage: "Photo de profil",
      titre: "Titre",
      description: "Description",
      prix: "Prix",
      prixTotal: "Prix total",
      etoiles: "Étoiles",
      ville: "Ville",
      villes: "Villes",
      adresse: "Adresse",
      telephone: "Téléphone",
      duree: "Durée",
      inclus: "Inclus",
      inclusions: "Inclusions",
      exclusions: "Exclusions",
      dateArrivee: "Date d'arrivée",
      dateDepart: "Date de départ",
      dateDebut: "Date de début",
      dateFin: "Date de fin",
      nombrePersonnes: "Nombre de personnes",
      nombreAdultes: "Nombre d'adultes",
      nombreEnfants: "Nombre d'enfants",
      statut: "Statut",
      reference: "Référence",
      client: "Client",
      compte: "Client",
      utilisateur: "Client",
      circuit: "Circuit",
      hotel: "Hôtel",
      transport: "Transport",
      informationsVoyageurs: "Informations des voyageurs",
      commentaires: "Commentaires",
      nationalite: "Nationalité",
      numeroPasseport: "Numéro de passeport",
      expirationPasseport: "Expiration du passeport",
      dateNaissance: "Date de naissance",
      stripeSessionId: "ID de session Stripe",
      stripeCustomerId: "ID client Stripe",
      paiement: "Paiement",
      methode: "Méthode",
      transactionId: "ID de transaction",
      montantPaye: "Montant payé",
      datePaiement: "Date de paiement",
      annulation: "Annulation",
      dateAnnulation: "Date d'annulation",
      raison: "Raison",
      remboursement: "Remboursement",
      montantRembourse: "Montant remboursé",
      nb_personnes: "Nombre de personnes",
      prix_total: "Prix total",
      nb_etoiles: "Nombre d'étoiles",
      type: "Type",
      modele: "Modèle",

      // ✅ NEW
      etapes: "Étapes (Hôtels)",
      hotelsByVille: "Hôtels par ville",
      roomTypes: "Types de Chambres",
    };

    return (
      translations[fieldName] ||
      fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/_/g, " ")
    );
  };

  // ✅ UI: rendu d'une étape ville + hotel
  const renderCircuitEtapes = (etapes, hotelsByVille) => {
    const safeEtapes = Array.isArray(etapes) ? etapes : [];

    // support fallback si tu n'as pas etapes mais tu as hotelsByVille
    const fromHotelsByVille =
      !safeEtapes.length && hotelsByVille && typeof hotelsByVille === "object"
        ? Object.entries(hotelsByVille)
          .filter(([ville, obj]) => ville && obj)
          .map(([ville, obj]) => ({
            ville,
            hotel: {
              _id: obj.hotelId,
              nom: obj.nom,
              image: obj.image,
            },
          }))
        : [];

    const list = safeEtapes.length ? safeEtapes : fromHotelsByVille;

    if (!Array.isArray(list) || list.length === 0) {
      return <span className="text-slate-500">-</span>;
    }

    return (
      <div className="space-y-3">
        {list.map((step, idx) => {
          const ville = step?.ville || "-";
          const hotel = step?.hotel;

          // populated hotel object or just id string
          const hotelObj =
            hotel && typeof hotel === "object"
              ? hotel
              : hotel
                ? { _id: String(hotel) }
                : null;

          const hotelName = hotelObj?.nom || "Hôtel (non populé)";
          const hotelImage = resolveImageUrl(hotelObj?.image);

          return (
            <div
              key={`${ville}-${idx}`}
              className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm"
            >
              <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 flex items-center justify-between">
                <div className="font-semibold text-slate-800 flex items-center gap-2">
                  <span>📍</span>
                  <span>{ville}</span>
                </div>
                {hotelObj?._id && (
                  <span className="text-[11px] text-slate-500">ID: {hotelObj._id}</span>
                )}
              </div>

              <div className="p-4">
                <div className="flex gap-4 items-start">
                  <div className="w-28 h-20 rounded-lg bg-slate-100 overflow-hidden border border-slate-200 flex-shrink-0">
                    {hotelImage ? (
                      <img
                        src={hotelImage}
                        alt={hotelName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src =
                            "https://via.placeholder.com/240x160?text=Image+non+disponible";
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">
                        Pas d’image
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 truncate">
                      🏨 {hotelName}
                    </div>

                    <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-slate-700">
                      {hotelObj?.nb_etoiles ? (
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500">Étoiles:</span>
                          <span>{"⭐".repeat(Number(hotelObj.nb_etoiles) || 0)}</span>
                        </div>
                      ) : null}

                      {hotelObj?.prix ? (
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500">Prix/nuit:</span>
                          <span className="font-semibold">{hotelObj.prix} MAD</span>
                        </div>
                      ) : null}

                      {hotelObj?.ville ? (
                        <div className="flex items-center gap-2 col-span-2">
                          <span className="text-slate-500">Ville hôtel:</span>
                          <span className="font-medium">{hotelObj.ville}</span>
                        </div>
                      ) : null}
                    </div>

                    {!hotelObj?.nom && (
                      <div className="mt-2 text-xs text-orange-700 bg-orange-50 border border-orange-100 p-2 rounded-lg">
                        ⚠️ Pour afficher le nom + image automatiquement, fais un{" "}
                        <b>populate("etapes.hotel")</b> côté backend.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const formatValue = (value, field) => {
    if (value === null || value === undefined) return "-";

    // Dates
    if (
      field.includes("date") ||
      field.includes("Date") ||
      field === "createdAt" ||
      field === "updatedAt"
    ) {
      try {
        return format(new Date(value), "dd MMMM yyyy HH:mm", { locale: fr });
      } catch {
        return value;
      }
    }

    // Booléens
    if (typeof value === "boolean") return value ? "✅ Oui" : "❌ Non";

    // ✅ NEW: afficher etapes proprement
    if (field === "etapes") {
      // value = [{ ville, hotel }]
      return renderCircuitEtapes(value, item?.hotelsByVille);
    }

    // ✅ NEW: afficher hotelsByVille si on l'a en DB (fallback)
    if (field === "hotelsByVille") {
      return renderCircuitEtapes([], value);
    }

    // ✅ NEW: Types de chambres
    if (field === "roomTypes" && Array.isArray(value)) {
      if (value.length === 0) return "-";
      return (
        <div className="space-y-6">
          {value.map((room, idx) => (
            <div
              key={idx}
              className="bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden shadow-sm"
            >
              <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100 flex items-center justify-between">
                <div className="font-bold text-slate-800 flex items-center gap-2">
                  <span>🛏️</span>
                  <span>{room.name || "Type de chambre"}</span>
                </div>
                <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-sm">
                  {room.price || 0} MAD / nuit
                </div>
              </div>

              <div className="p-4">
                {room.images && Array.isArray(room.images) && room.images.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {room.images.map((url, imgIdx) => {
                      const imgUrl = resolveImageUrl(url);
                      return (
                        <div
                          key={imgIdx}
                          className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 group"
                        >
                          <img
                            src={imgUrl}
                            alt={`${room.name} ${imgIdx}`}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src =
                                "https://via.placeholder.com/240x160?text=Indisponible";
                            }}
                          />
                          <a
                            href={imgUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"
                            aria-label="Voir l'image"
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-4 text-xs text-slate-400 italic bg-white/50 rounded-xl border border-dashed border-slate-200">
                    Aucune photo pour ce type de chambre
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // ✅ NEW: Album photos
    if (field === "images" && Array.isArray(value)) {
      if (value.length === 0) return "-";
      return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {value.map((url, idx) => {
            const imgUrl = resolveImageUrl(url);
            return (
              <div
                key={idx}
                className="relative group aspect-square rounded-xl overflow-hidden border border-slate-200 shadow-sm"
              >
                <img
                  src={imgUrl}
                  alt={`Aperçu ${idx + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      "https://via.placeholder.com/240x160?text=Image+non+disponible";
                  }}
                />
                  <a
                    href={imgUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"
                    title="Ouvrir l'image"
                    aria-label="Ouvrir l'image"
                  />
              </div>
            );
          })}
        </div>
      );
    }

    // Tableaux
    if (Array.isArray(value)) {
      if (value.length === 0) return "-";

      // tableaux d'objets
      if (typeof value[0] === "object") {
        return (
          <div className="space-y-2">
            {value.map((obj, idx) => (
              <div key={idx} className="bg-slate-50 p-2 rounded-lg text-sm">
                {Object.entries(obj).map(([key, val]) => (
                  <div key={key} className="flex justify-between items-start">
                    <span className="font-medium">{getFieldDisplayName(key)}:</span>
                    <span className="ml-2">{val || "-"}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        );
      }

      // tableaux simples
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((val, idx) => (
            <span
              key={idx}
              className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs"
            >
              {val}
            </span>
          ))}
        </div>
      );
    }

    // Objets imbriqués
    if (typeof value === "object") {
      if (Object.keys(value).length === 0) return "-";

      // client/compte/utilisateur
      if (field === "client" || field === "compte" || field === "utilisateur") {
        return (
          <div className="bg-blue-50 p-3 rounded-lg text-sm space-y-2 border border-blue-100">
            <div className="font-semibold text-blue-800 border-b border-blue-100 pb-1 mb-2">
              Informations client
            </div>
            {value.nom && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Nom:</span>
                <span className="ml-2">{value.nom}</span>
              </div>
            )}
            {value.prenom && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Prénom:</span>
                <span className="ml-2">{value.prenom}</span>
              </div>
            )}
            {value.email && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Email:</span>
                <span className="ml-2 text-blue-600">{value.email}</span>
              </div>
            )}
            {value.telephone && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Téléphone:</span>
                <span className="ml-2">{value.telephone}</span>
              </div>
            )}
            {value._id && (
              <div className="flex justify-between items-start text-xs text-slate-500 pt-1 border-t border-blue-100 mt-2">
                <span>ID Client:</span>
                <span className="ml-2">{value._id}</span>
              </div>
            )}
          </div>
        );
      }

      // hôtel
      if (field === "hotel") {
        const imageUrl = value.image ? resolveImageUrl(value.image) : "";
        return (
          <div className="bg-green-50 p-3 rounded-lg text-sm space-y-2 border border-green-100">
            <div className="font-semibold text-green-800 border-b border-green-100 pb-1 mb-2">
              Détails de l'hôtel
            </div>
            {imageUrl && (
              <div className="flex justify-center mb-3">
                <img
                  src={imageUrl}
                  alt={value.nom || "Hôtel"}
                  className="w-full max-w-xs h-32 object-cover rounded-lg border border-slate-200 shadow-sm"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      "https://via.placeholder.com/240x160?text=Image+non+disponible";
                  }}
                />
              </div>
            )}
            {value.nom && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Nom:</span>
                <span className="ml-2">{value.nom}</span>
              </div>
            )}
            {value.nb_etoiles && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Étoiles:</span>
                <span className="ml-2">{"⭐".repeat(value.nb_etoiles)}</span>
              </div>
            )}
            {value.ville && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Ville:</span>
                <span className="ml-2">{value.ville}</span>
              </div>
            )}
            {value.prix && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Prix/nuit:</span>
                <span className="ml-2">{value.prix} MAD</span>
              </div>
            )}
            {value._id && (
              <div className="flex justify-between items-start text-xs text-slate-500 pt-1 border-t border-green-100 mt-2">
                <span>ID Hôtel:</span>
                <span className="ml-2">{value._id}</span>
              </div>
            )}
          </div>
        );
      }

      // ✅ circuit (réservation -> circuit)
      if (field === "circuit") {
        const imageUrl = value.image ? resolveImageUrl(value.image) : "";
        return (
          <div className="bg-yellow-50 p-3 rounded-lg text-sm space-y-3 border border-yellow-100">
            <div className="font-semibold text-yellow-800 border-b border-yellow-100 pb-1 mb-2">
              Détails du circuit
            </div>

            {imageUrl && (
              <div className="flex justify-center mb-1">
                <img
                  src={imageUrl}
                  alt={value.titre || "Circuit"}
                  className="w-full max-w-xs h-32 object-cover rounded-lg border border-slate-200 shadow-sm"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      "https://via.placeholder.com/240x160?text=Image+non+disponible";
                  }}
                />
              </div>
            )}

            {value.titre && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Titre:</span>
                <span className="ml-2">{value.titre}</span>
              </div>
            )}
            {value.duree && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Durée:</span>
                <span className="ml-2">{value.duree}</span>
              </div>
            )}
            {value.prix && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Prix:</span>
                <span className="ml-2">{value.prix} MAD</span>
              </div>
            )}

            {value.villes && Array.isArray(value.villes) && value.villes.length > 0 && (
              <div className="flex flex-col items-start">
                <span className="font-medium mb-1">Villes:</span>
                <div className="flex flex-wrap gap-1 w-full">
                  {value.villes.map((ville, idx) => (
                    <span
                      key={idx}
                      className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs"
                    >
                      {ville}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* ✅ NEW: afficher les hôtels du circuit */}
            <div className="pt-2">
              <div className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <span>🏨</span>
                <span>Hôtels du circuit</span>
              </div>
              {renderCircuitEtapes(value?.etapes, value?.hotelsByVille)}
            </div>

            {value._id && (
              <div className="flex justify-between items-start text-xs text-slate-500 pt-1 border-t border-yellow-100 mt-2">
                <span>ID Circuit:</span>
                <span className="ml-2">{value._id}</span>
              </div>
            )}
          </div>
        );
      }

      // transport
      if (field === "transport") {
        const imageUrl = value.image ? resolveImageUrl(value.image) : "";
        return (
          <div className="bg-purple-50 p-3 rounded-lg text-sm space-y-2 border border-purple-100">
            <div className="font-semibold text-purple-800 border-b border-purple-100 pb-1 mb-2">
              Détails du transport
            </div>
            {imageUrl && (
              <div className="flex justify-center mb-3">
                <img
                  src={imageUrl}
                  alt={value.titre || value.type || "Transport"}
                  className="w-full max-w-xs h-32 object-cover rounded-lg border border-slate-200 shadow-sm"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      "https://via.placeholder.com/240x160?text=Image+non+disponible";
                  }}
                />
              </div>
            )}
            {value.type && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Type:</span>
                <span className="ml-2">{value.type}</span>
              </div>
            )}
            {value.titre && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Titre:</span>
                <span className="ml-2">{value.titre}</span>
              </div>
            )}
            {value.modele && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Modèle:</span>
                <span className="ml-2">{value.modele}</span>
              </div>
            )}
            {value.prix && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Prix:</span>
                <span className="ml-2">{value.prix} MAD</span>
              </div>
            )}
            {value._id && (
              <div className="flex justify-between items-start text-xs text-slate-500 pt-1 border-t border-purple-100 mt-2">
                <span>ID Transport:</span>
                <span className="ml-2">{value._id}</span>
              </div>
            )}
          </div>
        );
      }

      // paiement
      if (field === "paiement") {
        return (
          <div className="bg-emerald-50 p-3 rounded-lg text-sm space-y-2 border border-emerald-100">
            <div className="font-semibold text-emerald-800 border-b border-emerald-100 pb-1 mb-2">
              Détails du paiement
            </div>
            {value.methode && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Méthode:</span>
                <span className="ml-2">{value.methode}</span>
              </div>
            )}
            {value.montantPaye && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Montant payé:</span>
                <span className="ml-2">{value.montantPaye} MAD</span>
              </div>
            )}
            {value.transactionId && (
              <div className="flex justify-between items-start">
                <span className="font-medium">ID Transaction:</span>
                <span className="ml-2">{value.transactionId}</span>
              </div>
            )}
            {value.datePaiement && (
              <div className="flex justify-between items-start">
                <span className="font-medium">Date:</span>
                <span className="ml-2">
                  {format(new Date(value.datePaiement), "dd MMMM yyyy HH:mm", {
                    locale: fr,
                  })}
                </span>
              </div>
            )}
          </div>
        );
      }

      // objets génériques
      const objEntries = Object.entries(value).filter(
        ([key]) => key !== "_id" && key !== "__v" && typeof value[key] !== "object"
      );

      if (objEntries.length === 0) {
        if (value._id) return `ID: ${value._id}`;
        return "Objet";
      }

      return (
        <div className="bg-slate-50 p-2 rounded-lg text-sm space-y-1 border border-slate-200">
          {objEntries.map(([key, val]) => (
            <div key={key} className="flex justify-between items-start">
              <span className="font-medium">{getFieldDisplayName(key)}:</span>
              <span className="ml-2">{val || "-"}</span>
            </div>
          ))}
        </div>
      );
    }

    // Prix et montants
    if (
      (field.includes("prix") ||
        field.includes("montant") ||
        field === "amount" ||
        field.includes("Price")) &&
      !isNaN(value)
    ) {
      return `${value} MAD`;
    }

    // Images
    if (
      (field.includes("image") || field === "image" || field === "profileImage") &&
      typeof value === "string"
    ) {
      const imageUrl = resolveImageUrl(value);
      return (
        <div className="flex flex-col items-center">
          <img
            src={imageUrl}
            alt="Aperçu"
            className="w-full max-w-xs object-cover rounded-lg shadow-sm border border-slate-200"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://via.placeholder.com/240x160?text=Image+non+disponible";
            }}
          />
          <span className="text-xs text-slate-500 mt-1">
            {value.substring(value.lastIndexOf("/") + 1)}
          </span>
        </div>
      );
    }

    // Textes longs
    if (typeof value === "string" && value.length > 100) {
      return (
        <div className="relative max-h-32 overflow-y-auto text-sm">
          <p className="whitespace-pre-wrap">{value}</p>
        </div>
      );
    }

    return value.toString();
  };

  const categorizeFields = () => {
    const fields = Object.keys(item).filter((key) => key !== "mdp" && key !== "password");

    const categories = {
      main: [],
      dates: [],
      client: [],
      details: [],
      payment: [],
      other: [],
    };

    fields.forEach((field) => {
      // références en haut
      if (["client", "compte", "utilisateur", "hotel", "circuit", "transport", "voiture", "visite"].includes(field)) {
        categories.details.push(field);
      } else if (["_id", "reference", "statut", "titre", "nom"].includes(field)) {
        categories.main.push(field);
      } else if (field.toLowerCase().includes("date") || field === "createdAt" || field === "updatedAt") {
        categories.dates.push(field);
      } else if (
        field.includes("prix") ||
        field.includes("montant") ||
        field.includes("paiement") ||
        field === "stripeSessionId" ||
        field === "prix_total"
      ) {
        categories.payment.push(field);
      } else if (
        ["description", "commentaires", "nombrePersonnes", "nombreAdultes", "nombreEnfants", "nb_personnes", "informationsVoyageurs"].includes(
          field
        )
      ) {
        categories.details.push(field);
      } else if (field === "etapes" || field === "hotelsByVille") {
        // ✅ NEW: on met etapes dans details
        categories.details.push(field);
      } else {
        categories.other.push(field);
      }
    });

    return categories;
  };

  const categories = categorizeFields();

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] p-4 pt-20 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[75vh] overflow-hidden my-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 via-teal-500 to-emerald-500 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <span className="text-xl">🔍</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  Détails {item.reference || item.titre || item.nom || "de l'élément"}
                </h2>
                <p className="text-white/90 text-xs">{item._id && `ID: ${item._id}`}</p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-xl backdrop-blur-sm transition-all duration-300"
            >
              <span className="text-white text-lg">❌</span>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(75vh-140px)] divide-y divide-slate-200 space-y-6">
          {/* Main */}
          {categories.main.length > 0 && (
            <div className="pb-6">
              <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center">
                <span className="text-lg mr-2">📌</span>
                Informations principales
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {categories.main.map((field) => (
                  <div key={field} className="bg-slate-50 p-4 rounded-xl">
                    <div className="text-sm text-slate-500 mb-1">{getFieldDisplayName(field)}</div>
                    <div className="font-semibold">
                      {field === "statut" ? (
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full 
                          ${item[field] === "confirmé"
                              ? "bg-green-100 text-green-800"
                              : item[field] === "en attente"
                                ? "bg-yellow-100 text-yellow-800"
                                : item[field] === "payé"
                                  ? "bg-blue-100 text-blue-800"
                                  : item[field] === "annulé"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-slate-100 text-slate-800"
                            }`}
                        >
                          {item[field]}
                        </span>
                      ) : (
                        formatValue(item[field], field)
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dates */}
          {categories.dates.length > 0 && (
            <div className="pt-6 pb-6">
              <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center">
                <span className="text-lg mr-2">📅</span>
                Dates
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {categories.dates.map((field) => (
                  <div key={field} className="bg-slate-50 p-4 rounded-xl">
                    <div className="text-sm text-slate-500 mb-1">{getFieldDisplayName(field)}</div>
                    <div className="font-medium">{formatValue(item[field], field)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Client */}
          {categories.client.length > 0 && (
            <div className="pt-6 pb-6">
              <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center">
                <span className="text-lg mr-2">👤</span>
                Informations client
              </h3>

              <div className="grid grid-cols-1 gap-4">
                {categories.client.map((field) => (
                  <div key={field} className="bg-slate-50 p-4 rounded-xl">
                    <div className="text-sm text-slate-500 mb-1">{getFieldDisplayName(field)}</div>
                    <div className="font-medium">{formatValue(item[field], field)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Details */}
          {categories.details.length > 0 && (
            <div className="pt-6 pb-6">
              <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center">
                <span className="text-lg mr-2">📝</span>
                Détails
              </h3>

              <div className="grid grid-cols-1 gap-4">
                {categories.details.map((field) => (
                  <div key={field} className="bg-slate-50 p-4 rounded-xl">
                    <div className="text-sm text-slate-500 mb-2">{getFieldDisplayName(field)}</div>
                    <div className="font-medium">{formatValue(item[field], field)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment */}
          {categories.payment.length > 0 && (
            <div className="pt-6 pb-6">
              <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center">
                <span className="text-lg mr-2">💰</span>
                Informations de paiement
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {categories.payment.map((field) => (
                  <div key={field} className="bg-slate-50 p-4 rounded-xl">
                    <div className="text-sm text-slate-500 mb-1">{getFieldDisplayName(field)}</div>
                    <div className="font-medium">{formatValue(item[field], field)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Other */}
          {categories.other.length > 0 && (
            <div className="pt-6">
              <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center">
                <span className="text-lg mr-2">🔍</span>
                Autres informations
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {categories.other.map((field) => (
                  <div key={field} className="bg-slate-50 p-4 rounded-xl">
                    <div className="text-sm text-slate-500 mb-1">{getFieldDisplayName(field)}</div>
                    <div className="font-medium">{formatValue(item[field], field)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row gap-3 p-6 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2"
          >
            <span>✖️</span>
            Fermer
          </button>

          {item._id && (
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(item._id);
                alert("ID copié dans le presse-papiers !");
              }}
              className="px-6 py-3 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2"
            >
              <span>📋</span>
              Copier l'ID
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDetailsModal;
